import { useEffect, useRef, useState, type ReactNode } from 'react';
import { type SignupResponse } from '@hungryroot/rest';
import {
  googleAuth,
  googleInitialize,
  createGoogleAuthButton,
  loadGoogleSdk,
  type Google,
} from '../../provider/google';
import { GoogleIcon } from '../../svg/GoogleIcon';
import { RoundedButton } from '../RoundedButton/RoundedButton';

import styles from './ButtonGoogle.module.css';

type Props = {
  onLoginSuccess: (data: SignupResponse) => void;
  onSignupSuccess: (data: SignupResponse) => void;
  onError: () => void;
  /** Marks a freemium (guest-flow) signup if this auth creates an account. */
  isGuestFlow?: boolean;
  children?: ReactNode;
};

export function ButtonGoogle({
  onLoginSuccess,
  onError,
  onSignupSuccess,
  isGuestFlow,
  children,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [googleSdk, setGoogleSdk] = useState<Google | null>(null);
  const [googleAuthFn, setGoogleAuthFn] = useState<(() => void) | null>(null);

  useEffect(() => {
    loadGoogleSdk({
      onReady: setGoogleSdk,
    });
  }, []);

  useEffect(() => {
    if (!googleSdk) {
      return;
    }
    googleInitialize(googleSdk, {
      onAuthRequest(credential) {
        googleAuth({
          onSignupSuccess,
          onLoginSuccess,
          onError,
          credential,
          isGuestFlow,
        });
      },
    });
  }, [googleSdk, onLoginSuccess, onError, onSignupSuccess, isGuestFlow]);

  useEffect(() => {
    const { current } = containerRef;
    if (current === null || !googleSdk) {
      return;
    }

    const authFn = createGoogleAuthButton(googleSdk, current);
    setGoogleAuthFn(() => authFn);
  }, [googleSdk]);

  return (
    <RoundedButton
      disabled={googleAuthFn === null}
      onClick={() => {
        if (googleAuthFn === null) {
          return;
        }
        googleAuthFn();
      }}
      aria-label="Sign up with Google"
    >
      <GoogleIcon />
      {children}
      <div
        className={styles['native-button-container']}
        ref={containerRef}
      ></div>
    </RoundedButton>
  );
}
